/**
 * Several windows of the desktop app, one project in each (owner's request, 09/10/2026; docs/refactor/28): like two
 * Cursor windows. One app, not several — the main window owns what must happen once per computer (taking work, keeping
 * awake, the inventory, the notifications); every other window is a view on its own project.
 *
 * A window opened for a project starts with `?w=<label>&repo=<id>`; both are kept for that window only (session
 * storage is per window), so choosing a project in one window does not change the other.
 */

import { isDesktop } from './desktop'

const WINDOW_KEY = 'devpulse.window'
/** The project of this window, kept per window (lib/activeRepository.ts reads it first). */
export const WINDOW_REPOSITORY_KEY = 'devpulse.window.activeRepository'

function sessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function sessionSet(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Private storage: this window still opens on its project, it just forgets it on reload.
  }
}

/** Reads `?w=` and `?repo=` once, when a window opens. */
export function adoptWindowParams(search: string = typeof location === 'undefined' ? '' : location.search) {
  const params = new URLSearchParams(search)
  const label = params.get('w')
  const repo = params.get('repo')
  if (label && /^w-[A-Za-z0-9-]{1,40}$/.test(label)) sessionSet(WINDOW_KEY, label)
  if (repo && /^[0-9a-f-]{36}$/i.test(repo)) sessionSet(WINDOW_REPOSITORY_KEY, repo)
}

/** This window's own label, or null for the main window (and in a browser). */
export function windowLabel(): string | null {
  return isDesktop() ? sessionGet(WINDOW_KEY) : null
}

/** Whether this is the window that owns the once-per-computer work. In a browser, every tab is its own. */
export function isMainWindow(): boolean {
  return windowLabel() === null
}

/** Opens another window of the app, on a project when given (desktop only). */
export async function openNewWindow(repositoryId?: string | null): Promise<void> {
  if (!isDesktop()) return
  const { invoke } = await import('@tauri-apps/api/core')
  await invoke('window_new', { repository: repositoryId ?? null })
}
